import { Observable, Subscription } from "rxjs";

import { Component, Input, OnInit, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { Router, ActivatedRoute, Params } from "@angular/router";

import * as _ from 'lodash';

import { PagedListAdapter } from "../../../../common/paged-list-adapter";
import { PlannedService } from "../../../../models/plannedService";

import swal from "bootstrap-sweetalert";
import { ServiceObject } from "app/models/serviceObject";
import { PagedResult } from "app/models/PagedResult";
import { Work } from "app/models/work";
import { ScrollToAnchorService } from "app/services/scroll-to-anchor.service";

@Component({
  selector: 'plannedService-short-list',
  templateUrl: './plannedService-short-list.component.html'
})
export class PlannedServiceShortListComponent implements OnInit, OnDestroy {

  private _itemsSubscription: Subscription;
  private _itemsObservable: Observable<PagedResult<PlannedService>>;
  private _items: PlannedService[] = null;
  private _pagination = {
    id: 'plannedServices_pagination',
    itemsPerPage: 20,
    currentPage: 1,
    totalItems: 0
  };

  private _sortBy = "Name";
  private _isDesc = false;
  private _currentTime: Date = new Date();

  private _errorState: PlannedServiceShortListComponent.ErrorState = PlannedServiceShortListComponent.ErrorState.None;

  constructor(
    private _changeDetector: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private _scrollToAnchorService: ScrollToAnchorService) { }

  ngOnInit(): void {
    if (this.initialItemsPerPage != null)
      this._pagination.itemsPerPage = this.initialItemsPerPage;

    this.activatedRoute.queryParams.subscribe(params => {
      this._isDesc = params['isDesc'] ? JSON.parse(params['isDesc']) : true;
      this._sortBy = params['sortBy'] ? params['sortBy'] : "Name";
      this._pagination.itemsPerPage = params['count'] ? params['count'] : 10;
      this._pagination.currentPage = params['page'] ? params['page'] : 1;

      this.clearSubscription();

      this.loadPlannedServices();
    });
  }

  ngOnDestroy(): void {
    this.clearSubscription();
    swal.close();
  }

  @Input("adapter")
  public adapter: PagedListAdapter<PlannedService>;

  @Input("initialItemsPerPage")
  public initialItemsPerPage?: number = null;

  private sort(sortBy: string, isDesc: boolean) {
    this.setQueryParams(
      {
        sortBy: sortBy,
        isDesc: isDesc
      });
  }

  private setItemsPerPage(itemsPerPage: number): void {
    var firstPageItemIndex = this._pagination.itemsPerPage * (this._pagination.currentPage - 1);
    this.setQueryParams(
      {
        page: Math.floor(firstPageItemIndex / this._pagination.itemsPerPage) + 1,
        count: itemsPerPage
      });
  }

  private setPage(page: number) {
    this.setQueryParams({ page: page });
  }

  private setQueryParams(queryParams: object) {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        ...this.activatedRoute.snapshot.queryParams,
        ...queryParams
      }
    });
  }

  private isFinishedInTime(plannedService: PlannedService) {
    if (plannedService.status == PlannedService.Status.Planned) {
      if (this._currentTime > plannedService.deadline)
        return false;
      else
        return true;
    } else if (plannedService.status == PlannedService.Status.Done) {
      if ((plannedService.finishDate != null && plannedService.finishDate > plannedService.deadline))
        return false;
      else
        return true;
    }
    else
      return null;
  }

  private loadPlannedServices(): void {
    this._errorState = PlannedServiceShortListComponent.ErrorState.None;
    // WORKAROUND: без этой строчки изменение поля this._errorState не определяется в разметке
    this._changeDetector.markForCheck();

    this.clearSubscription();

    let itemsObservable = this.adapter.getItems(this._pagination.itemsPerPage, (this._pagination.currentPage - 1) * this._pagination.itemsPerPage, this._sortBy, this._isDesc);
    this._itemsObservable = itemsObservable;

    this._itemsSubscription = itemsObservable
      .subscribe(x => {
        // если на форме сейчас те элементы, которыми управляет данный Observable
        if (this._itemsObservable === itemsObservable) {
          this._items = x.items;
          this._pagination.totalItems = x.totalCount;
          this._changeDetector.markForCheck();
          this.restoreScrollPosition();
        }
      }, err => {
        // если на форме сейчас те элементы, которыми управляет данный Observable
        if (this._itemsObservable === itemsObservable) {
          this._errorState = PlannedServiceShortListComponent.ErrorState.UnknownError;
          // WORKAROUND: без этой строчки изменение поля this._errorState не определяется в разметке
          this._changeDetector.markForCheck();

          swal({
            title: 'Ошибка',
            text: 'Не удалось получить данные обращений. Попробуйте снова',
            type: 'error',
            confirmButtonText: 'Попробовать еще раз'
          }, () => {
            this.loadPlannedServices();
          });
        }
      });
  }

  private convertPlannedServiceStatusToText(status: PlannedService.Status): string {
    switch (status) {
      case PlannedService.Status.Planned:
        return "Запланировано";
      case PlannedService.Status.Done:
        return "Выполнено";
      case PlannedService.Status.Canceled:
        return "Отменено";
      default:
        return "";
    }
  }

  private getColorStatus(status: PlannedService.Status): string {
    switch (status) {
      case PlannedService.Status.Planned: return "label-warning";
      case PlannedService.Status.Done: return "label-success";
      case PlannedService.Status.Canceled: return "label-primary";
      default: return "label-default";
    }
  }

  private clearSubscription() {
    if (this._itemsSubscription)
      this._itemsSubscription.unsubscribe();

    this._itemsSubscription = null;
    this._itemsObservable = null;
    this._items = null;
  }

  private goToDetailPlannedService(elementId : string, plannedServiceId : number) {
    let currentUrl = this.router.url.split('#')[0];
    this.router.navigate([`/plannedService/${plannedServiceId}`]);
    window.history.replaceState({}, '', currentUrl + "#" + elementId);
  }

  private restoreScrollPosition() : void {
    const fragment = this.activatedRoute.snapshot.fragment;

    if (fragment) {
      let index = parseInt(fragment.split('_')[1]);
      if (index < this._items.length) {
        this._scrollToAnchorService.scroll(fragment);
      } else {
        this._scrollToAnchorService.scroll("row_" + (this._items.length - 1));
      }
    }
  }

  private ErrorStateEnum = PlannedServiceShortListComponent.ErrorState;
}

export namespace PlannedServiceShortListComponent {

  export enum ErrorState {
    /**
     * Нет ошибок
     */
    None,
    /**
     * Другая ошибка при получении данных с сервера
     */
    UnknownError
  }
}



// WEBPACK FOOTER //
// /app/SPA/src/app/client/components/plannedService/list/plannedService-short-list.component.ts